import { Badge } from "../../components/ui/badge";import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Settings,
  Plus,
  Trash2,
  Eye,
  GripVertical,
  Loader2,
  Save,
} from "lucide-react";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import CustomEditor from "../../components/ui/custom-text-editor";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { api } from "../../utils/api";

interface CustomField {
  id: string;
  label: string;
  type: "text" | "number" | "date" | "textarea";
  required: boolean;
}

interface OfferTemplate {
  id: number;
  name: string;
  subject: string;
  bodyContent: string;
  customFields: CustomField[];
  isDefault: boolean;
  isActive: boolean;
  isSystem: boolean;
  templateType: string | null;
}

const MANDATORY_FIELDS = [
  { key: "candidateName", label: "Candidate Name" },
  { key: "jobRole", label: "Job Role" },
  { key: "jobPosition", label: "Job Position" },
  { key: "location", label: "Location" },
];

const plainTextToHtml = (text: string): string => {
  if (!text) return "";
  // If already HTML, return as-is
  if (text.trim().startsWith("<") && text.trim().endsWith(">")) return text;
  return text
    .split("\n")
    .map((line) => (line.trim() ? `<p>${line}</p>` : "<p><br></p>"))
    .join("");
};

const EditOfferTemplate: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { id } = useParams();

  if (!ability.can(ACTIONS.UPDATE, RESOURCES.OFFER_TEMPLATE)) {
    return <AccessRestricted />;
  }

  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [isSystem, setIsSystem] = useState(false);
  const [templateType, setTemplateType] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  // Add field dialog
  const [showAddField, setShowAddField] = useState(false);
  const [fieldLabel, setFieldLabel] = useState("");
  const [fieldType, setFieldType] = useState<CustomField["type"]>("text");
  const [fieldRequired, setFieldRequired] = useState(false);

  // Fetch template on mount
  useEffect(() => {
    if (!id) return;
    setPageLoading(true);
    api
      .get<{ success: boolean; template: OfferTemplate }>(`/api/offer-templates/${id}`)
      .then((data) => {
        if (data.success) {
          const t = data.template;
          setTemplateName(t.name);
          setTemplateSubject(t.subject || "");
          setBodyContent(plainTextToHtml(t.bodyContent || ""));
          setIsSystem(t.isSystem);
          setTemplateType(t.templateType);
          // Ensure each custom field has an id for React keys
          setCustomFields(
            (t.customFields || []).map((f: any) => ({
              id: f.id || crypto.randomUUID(),
              label: f.label,
              type: f.type,
              required: f.required,
            }))
          );
          setIsDefault(t.isDefault);
          setIsActive(t.isActive);
        }
      })
      .catch((err) => {
        console.error("Failed to load template:", err);
        toast.error(err.message || "Failed to load template");
      })
      .finally(() => setPageLoading(false));
  }, [id]);

  const handleAddField = () => {
    if (!fieldLabel.trim()) {
      toast.error("Field label is required");
      return;
    }
    const newField: CustomField = {
      id: crypto.randomUUID(),
      label: fieldLabel.trim(),
      type: fieldType,
      required: fieldRequired,
    };
    setCustomFields((prev) => [...prev, newField]);
    setFieldLabel("");
    setFieldType("text");
    setFieldRequired(false);
    setShowAddField(false);
  };

  const handleRemoveField = (id: string) => {
    setCustomFields((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSubmit = async () => {
    if (!id) return;
    if (!templateName.trim()) {
      toast.error("Template name is required");
      return;
    }

    setLoading(true);
    try {
      const res = await api.put<{ success: boolean }>(`/api/offer-templates/${id}`, {
        name: templateName.trim(),
        subject: templateSubject.trim(),
        bodyContent: bodyContent.trim(),
        customFields: customFields.map((f) => ({
          id: f.id,
          label: f.label,
          type: f.type,
          required: f.required,
        })),
        isDefault,
        isActive,
      });
      if (res.success) {
        toast.success("Template updated successfully");
        navigate("/recruitment/settings");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to update template");
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-500">
        <Loader2 size={32} className="animate-spin text-gray-400" />
        <p className="mt-3 text-sm font-medium text-gray-500">Loading template...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Back Button */}
      <button
        onClick={() => navigate("/recruitment/settings")}
        className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors w-fit"
      >
        <ChevronLeft size={18} />
        <span className="text-sm font-medium">Back to Recruitment Settings</span>
      </button>

      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings size={28} className="text-gray-900" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Recruitment Settings
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            Manage organisations and offer templates
          </p>
        </div>
      </div>

      {/* Tabs (visual only - Offer Templates is active) */}
      <div className="bg-gray-100 p-1 rounded-full w-fit hidden md:flex h-auto">
        <button
          className="rounded-full px-6 py-2.5 text-slate-500 font-bold transition-all"
          onClick={() => navigate("/recruitment/settings")}
        >
          Organisations
        </button>
        <button className="rounded-full px-6 py-2.5 bg-white shadow-sm text-slate-900 font-bold transition-all">
          Offer Templates
        </button>
      </div>

      <div className="space-y-6">
        {/* Template Details */}
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-gray-900 text-base">Template Details</h4>
            {isSystem && (
              <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-700">
                System Template
              </span>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">
              Template Name <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="e.g., Senior Executive Offer"
              className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">
              Subject
            </label>
            <Input
              placeholder="e.g., Offer of Employment"
              className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
              value={templateSubject}
              onChange={(e) => setTemplateSubject(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-slate-950 focus:ring-slate-950"
              />
              <span className="text-sm font-medium text-gray-700">Set as default template</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-slate-950 focus:ring-slate-950"
              />
              <span className="text-sm font-medium text-gray-700">Active</span>
            </label>
          </div>
        </div>

        {/* Body Content Editor */}
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Template Body Content</h4>
              <p className="text-gray-500 text-sm font-medium mt-0.5">
                Edit the full offer letter text. Use {"{{placeholder}}"} syntax for dynamic fields.
              </p>
            </div>
          </div>
          <CustomEditor
            value={bodyContent}
            onChange={(value) => setBodyContent(value)}
            className="min-h-[400px]"
            placeholder="Enter the offer letter template content..."
          />
          <div className="p-3 rounded-xl bg-gray-50/50 border border-gray-100">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Available Placeholders</p>
            <div className="flex flex-wrap gap-1.5">
              {[
                "{{candidateName}}", "{{employeeName}}", "{{firstName}}", "{{lastName}}",
                "{{positionTitle}}", "{{department}}", "{{location}}", "{{companyName}}",
                "{{salary}}", "{{startDate}}", "{{expiryDate}}", "{{senderName}}",
                "{{senderTitle}}", "{{award}}", "{{probationPeriod}}", "{{noticePeriod}}",
                "{{restraintArea}}", "{{restraintPeriod}}", "{{entitlements}}", "{{offerDeadline}}",
                "{{reportingTo}}", "{{employeeAddress}}", "{{abn}}", "{{companyLocation}}",
                "{{baseHourlyRate}}", "{{hoursOfWork}}", "{{directorsName}}", "{{remuneration}}",
                "{{employmentStatus}}", "{{employmentType}}",
              ].map((ph) => (
                <span key={ph} className="text-[10px] font-mono bg-white border border-gray-200 rounded-md px-1.5 py-0.5 text-gray-600">
                  {ph}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Mandatory Fields */}
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
          <div>
            <h4 className="font-bold text-gray-900 text-base">Mandatory Fields</h4>
            <p className="text-gray-500 text-sm font-medium mt-0.5">
              These fields are always included in every offer
            </p>
          </div>
          <div className="space-y-2">
            {MANDATORY_FIELDS.map((field) => (
              <div
                key={field.key}
                className="flex items-center justify-between p-3 rounded-xl bg-gray-50/50"
              >
                <span className="text-sm font-bold text-gray-900">{field.label}</span>
                <Badge
                  variant="outline"
                  className="rounded-md border border-gray-200 font-bold px-2 py-0.5 text-[10px] bg-white text-gray-500"
                >
                  Required
                </Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Fields */}
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Custom Fields</h4>
              <p className="text-gray-500 text-sm font-medium mt-0.5">
                Add dynamic fields to this template
              </p>
            </div>
            <Button
              className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-9 rounded-xl text-xs font-bold px-4"
              onClick={() => setShowAddField(true)}
            >
              <Plus size={14} />
              <span>Add Field</span>
            </Button>
          </div>

          {customFields.length === 0 ? (
            <p className="text-sm text-gray-400 font-medium text-center py-6">
              No custom fields added yet. Click "Add Field" to create one.
            </p>
          ) : (
            <div className="space-y-2">
              {customFields.map((field) => (
                <div
                  key={field.id}
                  className="flex items-center justify-between p-3 rounded-xl bg-gray-50/50 border border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <GripVertical size={14} className="text-gray-300" />
                    <span className="text-sm font-bold text-gray-900">{field.label}</span>
                    <Badge
                      variant="outline"
                      className="rounded-md border-0 font-bold px-2 py-0.5 text-[10px] bg-gray-100 text-gray-500"
                    >
                      {field.type}
                    </Badge>
                    {field.required && (
                      <Badge
                        variant="outline"
                        className="rounded-md border border-gray-200 font-bold px-2 py-0.5 text-[10px] bg-white text-gray-500"
                      >
                        Required
                      </Badge>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveField(field.id)}
                    className="text-gray-300 hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview Offer */}
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-bold text-gray-900 text-base">Preview Offer</h4>
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 h-9 rounded-xl text-xs font-bold px-3"
              onClick={() => setShowPreview((v) => !v)}
            >
              <Eye size={14} />
              <span>{showPreview ? "Hide Preview" : "Show Preview"}</span>
            </Button>
          </div>

          {showPreview && (
            <div className="p-5 rounded-xl bg-gray-50/50 border border-gray-100 space-y-4">
              <p className="text-sm font-bold text-gray-900">
                Template: {templateName || "[Template Name]"}
              </p>
              <div className="border-t border-gray-200 pt-3">
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mb-2">
                  Mandatory Fields:
                </p>
                <ul className="space-y-1.5">
                  {MANDATORY_FIELDS.map((field) => (
                    <li key={field.key} className="text-sm text-gray-700 flex items-center gap-2">
                      <span className="text-gray-400">•</span>
                      {field.label}
                    </li>
                  ))}
                </ul>
              </div>
              {customFields.length > 0 && (
                <div className="border-t border-gray-200 pt-3">
                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mb-2">
                    Custom Fields:
                  </p>
                  <ul className="space-y-1.5">
                    {customFields.map((field) => (
                      <li key={field.id} className="text-sm text-gray-700 flex items-center gap-2">
                        <span className="text-gray-400">•</span>
                        {field.label}
                        {field.required && (
                          <span className="text-[10px] text-gray-400 font-medium">(required)</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button
            variant="outline"
            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-11 rounded-xl text-sm font-bold px-6"
            onClick={() => navigate("/recruitment/settings")}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-11 rounded-xl text-sm font-bold px-6"
            onClick={handleSubmit}
            disabled={loading}
          >
            <Save size={16} />
            <span>{loading ? "Updating..." : "Update Template"}</span>
          </Button>
        </div>
      </div>

      {/* Add Field Dialog */}
      <Dialog open={showAddField} onOpenChange={setShowAddField}>
        <DialogContent className="sm:max-w-[400px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-lg font-bold text-gray-900">Add Custom Field</DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Field Label <span className="text-red-500">*</span></label>
              <Input
                placeholder="e.g., Start Date"
                className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                value={fieldLabel}
                onChange={(e) => setFieldLabel(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Field Type</label>
              <Select value={fieldType} onValueChange={(v) => setFieldType(v as CustomField["type"])}>
                <SelectTrigger className="bg-gray-50 border-gray-100 rounded-lg h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="date">Date</SelectItem>
                  <SelectItem value="textarea">Textarea</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={fieldRequired}
                onChange={(e) => setFieldRequired(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-slate-950 focus:ring-slate-950"
              />
              <span className="text-sm font-medium text-gray-700">Required field</span>
            </label>
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button variant="outline" className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5" onClick={() => setShowAddField(false)}>
                Cancel
              </Button>
              <Button className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5" onClick={handleAddField}>
                Add Field
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditOfferTemplate;
